/* Rental agreement (Schedule 1) signing page - sections/rental-agreement-page.liquid.
   Needs assets/rental-signing-common.js (window.MCSRentalSigning) first.

   The page IS the agreement document, in the same order as the signed PDF
   (Workspace core/integrations/rental_trial_agreement_pdf.py): Customer,
   Equipment Rented, Terms, Signatory, Payment Method. Everything commercial
   is server-authoritative and read-only; the customer only fills the blanks
   (trading name, ABN, name, position held, signature) and picks a payment
   method.

   Flow: read the personal token (?t=, then kept in sessionStorage and taken
   out of the address bar), load the booking (GET {base}/rental-agreement/api/
   prefill), and on "Sign Agreement" either tokenise the card with eWAY CSE
   via /eway/token-customer FIRST (Credit Card), or - for Account / EFT -
   require the credit account application, which the customer completes on
   /pages/credit-account-application in a new tab (the server stores it; the
   prefill's account_application_submitted flag is re-read whenever this tab
   regains focus). Then POST {base}/rental-agreement/api/sign, which returns
   the signed PDF. A staff-prepared agreement whose prefill has
   require_payment: false (card or account already on file) hides the
   Payment Method section and signs without one. */
(function () {
  'use strict';

  var S = window.MCSRentalSigning;
  if (!S) return;
  var str = S.str;
  var escapeHtml = S.escapeHtml;
  var joinList = S.joinList;
  var longDate = S.longDate;
  var money = S.money;
  var focusQuietly = S.focusQuietly;
  var DEFAULT_PDF_NAME = 'My Coffee Shop Rental Agreement.pdf';
  var APPLICATION_PAGE = '/pages/credit-account-application';

  var init = function (root) {
    var config = S.readConfig(root);
    var prefillUrl = S.endpoint(config.baseUrl, config.prefillPath, 'rental-agreement/api/prefill');
    var signUrl = S.endpoint(config.baseUrl, config.signPath, 'rental-agreement/api/sign');
    var ewayUrl = S.endpoint(config.baseUrl, '', 'eway/token-customer');

    var q = function (selector, scope) { return (scope || root).querySelector(selector); };
    var qa = function (selector, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(selector)); };

    /* ---------- Page states ---------- */
    var intro = q('[data-ra-intro]');
    var showState = S.createStates(root, function (name) {
      if (intro) intro.hidden = name !== 'form';
    });
    var showSigned = function (signedAt, moveFocus) {
      S.paintSignedDate(q('[data-ra-signed-date]'), signedAt);
      showState('signed', moveFocus);
    };

    var token = S.readToken();
    if (!token) {
      showState('missing');
      return;
    }

    /* ---------- Document elements ---------- */
    var form = q('[data-ra-form]');
    if (!form) return;
    var status = S.statusBox(q('[data-ra-status]'));
    var submitButton = q('[data-ra-submit]');
    var submitText = q('[data-ra-submit-text]');
    var submitHint = q('[data-ra-submit-hint]');
    var submitLabel = submitText ? submitText.textContent : 'Sign Agreement';
    var prefill = {};
    var contact = { name: '', first: '', last: '', phone: '', email: '' };
    var submitting = false;

    var fieldValue = function (name) {
      var field = form.elements[name];
      return field ? str(field.value) : '';
    };
    var setField = function (name, value) {
      var field = form.elements[name];
      if (field) field.value = str(value);
    };
    var setText = function (key, value) {
      qa('[data-ra-text="' + key + '"]').forEach(function (el) { el.textContent = value; });
    };

    /* Custom validity feeds native validation (gates + reportValidity). */
    var wireCustomValidity = function (name, check, message) {
      var field = form.elements[name];
      if (!field || !field.addEventListener) return function () {};
      var sync = function () {
        field.setCustomValidity(!str(field.value) || check(field.value) ? '' : message);
      };
      field.addEventListener('input', sync);
      field.addEventListener('change', sync);
      sync();
      return sync;
    };
    var ABN_MESSAGE = 'Please enter a valid 11-digit ABN.';
    var syncAbnValidity = wireCustomValidity('abn', S.isValidAbn, ABN_MESSAGE);
    var syncNameValidity = wireCustomValidity('signer_name', S.looksLikeName, 'Please enter your full name.');
    var syncPositionValidity = wireCustomValidity('position_held', S.looksLikeName, 'Please enter your position.');
    wireCustomValidity('card_number', S.passesLuhn, 'Please check your card number.');
    var expiryValid = function () {
      var month = Number(fieldValue('card_expiry_month').replace(/\D/g, ''));
      var yearRaw = fieldValue('card_expiry_year').replace(/\D/g, '');
      if (!month || !yearRaw) return true; /* required/pattern report the gaps */
      var year = Number(yearRaw.length === 2 ? '20' + yearRaw : yearRaw);
      var now = new Date();
      return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
    };
    ['card_expiry_month', 'card_expiry_year'].forEach(function (name) {
      var field = form.elements[name];
      if (!field) return;
      field.addEventListener('input', function () {
        var yearField = form.elements.card_expiry_year;
        if (yearField) yearField.setCustomValidity(expiryValid() ? '' : 'This card has expired.');
      });
    });
    /* A wrong ABN is called out when the customer leaves the field, not on
       every keystroke. */
    var abnField = form.elements.abn;
    if (abnField) {
      abnField.addEventListener('blur', function () {
        if (str(abnField.value) && !S.isValidAbn(abnField.value)) S.setFieldError(abnField, ABN_MESSAGE);
      });
      abnField.addEventListener('input', function () {
        if (!str(abnField.value) || S.isValidAbn(abnField.value)) S.clearFieldError(abnField);
      });
    }

    /* ---------- Prefill ---------- */
    var renderDocument = function () {
      setText('business_name', str(prefill.business_name) || '-');
      var locality = [str(prefill.city), str(prefill.state), str(prefill.postcode)].filter(Boolean).join(' ');
      setText('address', [str(prefill.street_address), locality].filter(Boolean).join(', ') || '-');
      setText('contact_name', contact.name || '-');
      setText('email', contact.email || '-');

      var equipment = Array.isArray(prefill.equipment) ? prefill.equipment.map(str).filter(Boolean) : [];
      if (!equipment.length && str(prefill.machine)) equipment = [str(prefill.machine)];
      var equipmentEl = q('[data-ra-equipment]');
      if (equipmentEl) {
        equipmentEl.innerHTML = (equipment.length ? equipment : ['To be confirmed']).map(function (item) {
          return '<div class="ra-row"><span class="ra-row__label">Model \/ Serial No.<\/span>' +
            '<span class="ra-row__value">' + escapeHtml(item) + '<\/span><\/div>';
        }).join('');
      }
      /* Monthly is the contractual figure (rent is invoiced monthly); the
         weekly price the customer was quoted rides along in brackets. */
      var monthly = money(prefill.monthly_rental);
      var weekly = money(prefill.weekly_rental);
      setText('monthly', monthly && weekly ? monthly + ' (' + weekly + ' a week)' : (monthly || weekly || 'To be confirmed'));
      /* Staff-prepared agreements (has_trial set either way) may leave a date blank: TBD. */
      var staffPrepared = typeof prefill.has_trial === 'boolean';
      setText('trial_start', prefill.has_trial === false ? 'Not applicable, no trial' : (longDate(prefill.trial_start_date) || str(prefill.trial_start_display) || (staffPrepared ? 'TBD' : 'To be confirmed')));
      setText('rental_start', longDate(prefill.rental_start_date) || (staffPrepared ? 'TBD' : 'After your free trial'));
      if (str(prefill.rent_clause)) setText('rent_clause', prefill.rent_clause);
      setText('payment_note', prefill.has_trial === false
        ? 'How you’ll pay the rental from the rental start date.'
        : "How you'll pay the rental once your free trial ends. Nothing is charged during your trial.");
      setText('card_security', 'Card details are encrypted in your browser and stored securely with our payment provider.' +
        (prefill.has_trial === false ? '' : ' Nothing is charged during your free trial.'));
      setText('card_consent', prefill.has_trial === false
        ? 'I authorise My Coffee Shop to securely store this card with our payment provider and to charge rental fees to it from the rental start date.'
        : 'I authorise My Coffee Shop to securely store this card with our payment provider and to charge rental fees to it after my free trial ends.');
      setText('today', S.todayLong());
      /* Staff untick "Require credit card / account" when the customer
         already has one on file: no payment step to show or complete. */
      var paymentStep = q('[data-ra-payment-step]');
      if (paymentStep) paymentStep.hidden = !paymentRequired();
      if (!paymentRequired()) {
        paymentRadios.forEach(function (radio) { radio.checked = false; });
        syncPayment();
      }
    };

    /* account_application_submitted may arrive top-level or inside prefill. */
    var accountFlag = function (result) {
      return result.account_application_submitted === true ||
        !!(result.prefill && result.prefill.account_application_submitted === true);
    };

    var populate = function (result) {
      prefill = result.prefill || {};
      var first = str(prefill.first_name);
      var last = str(prefill.last_name);
      var name = str(prefill.contact_name) || (first + ' ' + last).trim();
      if (!first && !last && name) {
        var parts = name.split(/\s+/);
        first = parts.shift() || '';
        last = parts.join(' ');
      }
      contact = { name: name, first: first, last: last, phone: str(prefill.phone), email: str(prefill.email) };
      setField('trading_name', prefill.trading_name);
      setField('abn', prefill.abn);
      setField('signer_name', name);
      setField('position_held', '');
      [syncAbnValidity, syncNameValidity, syncPositionValidity].forEach(function (sync) { sync(); });
      renderDocument();
      setAccountSubmitted(accountFlag(result));
    };

    /* ---------- Signature pad ---------- */
    var sigCanvas = q('[data-ra-sig]');
    var sigClear = q('[data-ra-sig-clear]');
    var docSig = { drawn: false, onChange: function () { syncGates(); } };
    var docPad = null;
    /* Sized once the document is visible (a hidden canvas has no width). */
    var ensureDocPad = function () {
      if (docPad && docPad.ready) return;
      docPad = S.initSignaturePad(sigCanvas, docSig, 140);
    };
    if (sigClear) {
      sigClear.addEventListener('click', function () {
        if (docPad) docPad.clear();
      });
    }

    /* ---------- Payment method ---------- */
    var paymentRadios = qa('[data-ra-payment]');
    var paymentCcBlock = q('[data-ra-payment-cc]');
    var paymentEftBlock = q('[data-ra-payment-eft]');
    var ccFields = qa('[data-ra-cc-required]');
    var selectedMethod = function () {
      var selected = paymentRadios.filter(function (radio) { return radio.checked; })[0];
      return selected ? selected.value : '';
    };
    var paymentRequired = function () { return prefill.require_payment !== false; };
    var cardSelected = function () { return selectedMethod() === 'Credit Card'; };
    var eftSelected = function () { return selectedMethod() === 'Account / EFT'; };
    var syncPayment = function () {
      var showCard = cardSelected();
      if (paymentCcBlock) paymentCcBlock.hidden = !showCard;
      if (paymentEftBlock) paymentEftBlock.hidden = !eftSelected();
      ccFields.forEach(function (field) { field.required = showCard; });
    };
    paymentRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        syncPayment();
        syncGates();
      });
    });
    /* A successful tokenisation is kept so a sign retry (e.g. after a field
       error) doesn't store a duplicate eWAY token customer; any edit to a
       card field invalidates it. */
    var cardVerificationOk = false;
    ccFields.forEach(function (field) {
      var invalidate = function () { cardVerificationOk = false; };
      field.addEventListener('input', invalidate);
      field.addEventListener('change', invalidate);
    });

    /* ---------- Account / EFT: the separate credit account application ---------- */
    var accountSubmitted = false;
    var accountTodo = q('[data-ra-account-todo]');
    var accountDone = q('[data-ra-account-done]');
    /* Opens in a new tab so this tab's typed blanks and drawn signature
       survive; the token rides in the link (that page strips it again). */
    qa('[data-ra-account-link]').forEach(function (link) {
      link.href = APPLICATION_PAGE + '?t=' + encodeURIComponent(token);
    });
    var setAccountSubmitted = function (submitted) {
      accountSubmitted = !!submitted;
      if (accountTodo) accountTodo.hidden = accountSubmitted;
      if (accountDone) accountDone.hidden = !accountSubmitted;
      syncGates();
    };

    /* ---------- Gate: the one Sign Agreement button ---------- */
    var validField = function (name) {
      var field = form.elements[name];
      return !!field && !!str(field.value) && field.validity.valid;
    };
    var gateState = function () {
      var method = selectedMethod();
      var cardOk = !cardSelected() || ccFields.every(function (field) { return field.validity.valid; });
      var accountOk = !eftSelected() || accountSubmitted;
      var needed = paymentRequired();
      return {
        paymentNeeded: needed,
        abnOk: validField('abn'),
        tradingOk: !form.elements.trading_name || form.elements.trading_name.validity.valid,
        nameOk: validField('signer_name'),
        positionOk: validField('position_held'),
        signed: docSig.drawn,
        method: method,
        cardOk: cardOk,
        accountOk: accountOk,
        paymentOk: !needed || (!!method && cardOk && accountOk)
      };
    };
    var syncGates = function () {
      var gate = gateState();
      var ready = gate.abnOk && gate.tradingOk && gate.nameOk && gate.positionOk && gate.signed && gate.paymentOk;
      if (submitButton) submitButton.disabled = submitting || !ready;
      if (submitHint) {
        var todo = [];
        if (!gate.abnOk) todo.push('enter your ABN');
        if (!gate.nameOk) todo.push('enter your name');
        if (!gate.positionOk) todo.push('enter your position');
        if (!gate.signed) todo.push('sign in the signature box');
        if (gate.paymentNeeded && !gate.method) todo.push('choose a payment method');
        else if (!gate.cardOk) todo.push('complete your card details and tick the authorisation');
        else if (!gate.accountOk) todo.push('complete the credit account application');
        submitHint.textContent = submitting || !todo.length ? '' : 'To sign, please ' + joinList(todo) + '.';
      }
      return ready;
    };

    var fetchPrefill = function () {
      return S.fetchJson(prefillUrl + '?t=' + encodeURIComponent(token), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
    };
    var loadPrefill = function () {
      showState('loading');
      fetchPrefill().then(function (reply) {
        var response = reply.response;
        var result = reply.result;
        if (response.ok && result.success === true) {
          if (result.status === 'signed') {
            showSigned(result.signed_at, true);
            return;
          }
          populate(result);
          showState('form');
          ensureDocPad();
          return;
        }
        if (response.status === 404) {
          showState('expired', true);
        } else if (response.status === 503) {
          showState('disabled', true);
        } else {
          showState('error', true);
        }
      }).catch(function () {
        showState('error', true);
      });
    };
    var retryButton = q('[data-ra-retry]');
    if (retryButton) retryButton.addEventListener('click', loadPrefill);

    /* Coming back to this tab (e.g. from the application tab) re-reads the
       prefill for the account_application_submitted flag only - the typed
       blanks, signature and card details are left exactly as they were.
       focus and visibilitychange usually fire together, hence the throttle;
       failures are silent (the next return tries again). */
    var lastRefresh = 0;
    var refreshAccountFlag = function () {
      if (root.getAttribute('data-ra-current') !== 'form' || submitting) return;
      var now = Date.now();
      if (now - lastRefresh < 1500) return;
      lastRefresh = now;
      fetchPrefill().then(function (reply) {
        if (!reply.response.ok || reply.result.success !== true) return;
        if (root.getAttribute('data-ra-current') !== 'form') return;
        if (reply.result.status === 'signed') {
          showSigned(reply.result.signed_at, true);
          return;
        }
        setAccountSubmitted(accountFlag(reply.result));
      }).catch(function () { /* try again on the next return */ });
    };
    window.addEventListener('focus', refreshAccountFlag);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refreshAccountFlag();
    });

    /* ---------- Card tokenisation (eWAY CSE) ---------- */
    /* Same backend and payload shape as the trial page. Card details are
       encrypted in-browser and never touch our own endpoint in the clear.
       Resolves true on success. */
    var digits = function (name) { return fieldValue(name).replace(/\D/g, ''); };
    var verifyCard = function () {
      if (!config.cseKey || !window.eCrypt || typeof window.eCrypt.encryptValue !== 'function') {
        status.set('Secure card encryption did not load. Please refresh the page and try again.', 'error');
        return Promise.resolve(false);
      }
      var encryptedNumber = window.eCrypt.encryptValue(digits('card_number'), config.cseKey);
      var encryptedCvn = window.eCrypt.encryptValue(digits('card_cvn'), config.cseKey);
      if (!encryptedNumber || !encryptedCvn) {
        status.set('Secure card encryption failed. Please check your card details and try again.', 'error');
        return Promise.resolve(false);
      }
      var month = digits('card_expiry_month');
      var year = digits('card_expiry_year');
      var payload = {
        source: 'shopify-payment-verification',
        companyName: str(prefill.business_name),
        customer: {
          firstName: contact.first,
          lastName: contact.last,
          email: contact.email,
          phone: contact.phone,
          country: 'au'
        },
        card: {
          name: fieldValue('card_name'),
          number: encryptedNumber,
          expiryMonth: month.length === 1 ? '0' + month : month,
          expiryYear: year.length === 4 ? year.slice(-2) : year,
          cvn: encryptedCvn
        },
        consent: !!(form.elements.card_consent && form.elements.card_consent.checked),
        honeypot: fieldValue('hp_field')
      };
      status.set('Securing your card details…', 'info');
      return S.fetchJson(ewayUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (reply) {
        if (!reply.response.ok || reply.result.ok !== true) {
          status.set('We could not verify your card. Please check the details and try again, or choose Account / EFT.', 'error');
          return false;
        }
        cardVerificationOk = true;
        return true;
      }).catch(function () {
        status.set('We could not verify your card. Please try again or choose Account / EFT.', 'error');
        return false;
      });
    };

    /* ---------- Sign ---------- */
    var setSubmitting = function (value) {
      submitting = value;
      if (submitText) submitText.textContent = value ? 'Signing…' : submitLabel;
      form.setAttribute('aria-busy', value ? 'true' : 'false');
      syncGates();
    };

    var pdfUrl = '';
    var showSuccess = function (result) {
      var links = q('[data-ra-pdf-links]');
      var view = q('[data-ra-pdf-view]');
      var download = q('[data-ra-pdf-download]');
      var filename = str(result.filename) || DEFAULT_PDF_NAME;
      if (result.pdf_base64 && links && view && download && window.URL && URL.createObjectURL) {
        try {
          var binary = atob(String(result.pdf_base64));
          var bytes = new Uint8Array(binary.length);
          for (var i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
          if (pdfUrl) URL.revokeObjectURL(pdfUrl);
          pdfUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
          view.href = pdfUrl;
          download.href = pdfUrl;
          download.setAttribute('download', filename);
          links.hidden = false;
        } catch (error) {
          links.hidden = true;
        }
      }
      /* Card details have done their job; don't leave them in the DOM. */
      ['card_name', 'card_number', 'card_expiry_month', 'card_expiry_year', 'card_cvn'].forEach(function (name) { setField(name, ''); });
      showState('success', true);
    };

    var handleSignError = function (response, result) {
      if (response.status === 409 || result.error === 'already_signed') {
        showSigned(result.signed_at, true);
        return;
      }
      if (response.status === 404 || result.error === 'not_found') {
        showState('expired', true);
        return;
      }
      if (response.status === 429) {
        status.set('Too many attempts. Please wait a few minutes and try again, or call 1300 365 487.', 'error');
        return;
      }
      if (response.status === 503 || result.error === 'disabled') {
        status.set('Online signing is unavailable right now. Please call 1300 365 487 and we\'ll help you complete your agreement.', 'error');
        return;
      }
      if (response.status === 400) {
        var fields = result.fields && typeof result.fields === 'object' ? result.fields : {};
        var details = [];
        var firstInvalid = null;
        Object.keys(fields).forEach(function (name) {
          var message = str(fields[name]);
          var input = form.elements[name];
          if (input && input.tagName === 'INPUT' && input.type !== 'hidden' && input.type !== 'radio') {
            S.setFieldError(input, message || 'Please check this field.');
            if (!firstInvalid) firstInvalid = input;
          } else if (message) {
            details.push(message);
          }
        });
        if (fields.signature || result.error === 'missing_signature') {
          if (docPad) docPad.clear();
          if (!fields.signature) details.push('Please sign in the signature box again.');
        }
        if (fields.payment_method || fields.card_verified) cardVerificationOk = false;
        /* The server checks the stored application; if it says there isn't
           one, put the application link back. */
        if (fields.account_application || result.error === 'missing_account_application') setAccountSubmitted(false);
        status.set(str(result.message) || 'Please check the highlighted details and try again.', 'error', details);
        if (firstInvalid) focusQuietly(firstInvalid);
        return;
      }
      status.set('We couldn\'t submit your agreement. Please try again or call 1300 365 487.', 'error');
    };

    var postSign = function () {
      var method = selectedMethod();
      var body = new FormData();
      var add = function (name, value) { body.append(name, value == null ? '' : String(value)); };
      add('token', token);
      add('signature', sigCanvas && sigCanvas.toDataURL ? sigCanvas.toDataURL('image/png') : '');
      add('signer_name', fieldValue('signer_name'));
      add('position_held', fieldValue('position_held'));
      add('business_name', str(prefill.business_name));
      add('trading_name', fieldValue('trading_name'));
      add('abn', fieldValue('abn'));
      /* The document has no billing/accounts/address-line-2 questions; the
         contract still carries the fields. The account application is NOT
         sent: the server uses the one stored from the application page. */
      add('address_line2', '');
      add('billing_separate', 'no');
      add('billing_address', '');
      add('accounts_separate', 'no');
      add('accounts_name', '');
      add('accounts_phone', '');
      add('accounts_email', '');
      add('payment_method', method);
      add('card_verified', method === 'Credit Card' && cardVerificationOk ? 'true' : 'false');
      add('contact_first_name', contact.first);
      add('contact_last_name', contact.last);
      add('contact_phone', contact.phone);
      add('contact_email', contact.email);
      try { if (window.MCSJourney) window.MCSJourney.append(body); } catch (error) { /* funnel is best-effort */ }
      status.set('Signing your agreement…', 'info');
      return S.fetchJson(signUrl, {
        method: 'POST',
        body: body,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (reply) {
        if (reply.response.ok && reply.result.success === true) {
          status.clear();
          showSuccess(reply.result);
          return;
        }
        handleSignError(reply.response, reply.result);
      }).catch(function () {
        status.set('We couldn\'t reach our server. Please check your connection and try again, or call 1300 365 487.', 'error');
      });
    };

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (submitting) return;
      status.clear();
      if (!form.reportValidity()) return;
      var gate = gateState();
      if (!gate.signed) {
        status.set('Please sign in the signature box.', 'error');
        focusQuietly(sigClear);
        return;
      }
      if (gate.paymentNeeded && !gate.method) {
        status.set('Please choose a payment method.', 'error');
        focusQuietly(paymentRadios[0]);
        return;
      }
      if (!gate.accountOk) {
        status.set('Please complete the credit account application first.', 'error');
        focusQuietly(q('[data-ra-account-link]'));
        return;
      }
      setSubmitting(true);
      var verification = cardSelected() && !cardVerificationOk ? verifyCard() : Promise.resolve(true);
      verification.then(function (ok) {
        if (!ok) return null;
        return postSign();
      }).then(function () {
        setSubmitting(false);
      });
    });

    form.addEventListener('input', function (event) {
      var target = event.target;
      if (target && target !== abnField && target.getAttribute && target.getAttribute('aria-invalid')) S.clearFieldError(target);
      syncGates();
    });
    form.addEventListener('change', function () { syncGates(); });

    syncPayment();
    syncGates();
    loadPrefill();
  };

  Array.prototype.slice.call(document.querySelectorAll('[data-ra-root]')).forEach(init);
})();
